'use client';

import { useEffect } from 'react';

const AUTH_REFRESH_MATCHERS = ['/auth/v1/token', 'grant_type=refresh_token'];
const STORAGE_CLEAN_FLAG = 'mh_auth_storage_cleaned';
const SHOULD_CLEAN_AUTH_STORAGE = process.env.NEXT_PUBLIC_AUTH_TRACE_CLEANUP === '1';

function traceMark(message: string): void {
  console.log('[AUTH STORM TRACE]', {
    mark: message,
    time: new Date().toISOString(),
  });
}

function shouldTraceRefresh(url?: string | null): boolean {
  if (!url) return false;
  return AUTH_REFRESH_MATCHERS.some((matcher) => url.includes(matcher));
}

function safeStorageKeys(storage: Storage | undefined): string[] {
  if (!storage) return [];
  try {
    return Object.keys(storage).slice(0, 200);
  } catch {
    return [];
  }
}

function logAuthStormTrace(url: string): void {
  console.error('[AUTH STORM TRACE]', {
    url,
    time: new Date().toISOString(),
    href: window.location.href,
    pathname: window.location.pathname,
    localStorageKeys: safeStorageKeys(window.localStorage),
    sessionStorageKeys: safeStorageKeys(window.sessionStorage),
    referrer: document.referrer,
  });
  console.trace('[AUTH STORM TRACE] stack');
}

function shouldRemoveStorageKey(key: string): boolean {
  return /^sb-.*auth/i.test(key) ||
    (key.toLowerCase().includes('supabase') && key.toLowerCase().includes('auth'));
}

function clearAuthStorage(storage: Storage, label: string, isDev: boolean): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (!key) continue;
    if (shouldRemoveStorageKey(key)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => {
    try {
      storage.removeItem(key);
    } catch (error) {
      if (isDev) {
        console.warn(`[Auth Trace] Failed to remove ${label} key`, { key, error });
      }
    }
  });

  if (isDev && keysToRemove.length > 0) {
    console.log(`[Auth Trace] Cleared ${label} auth storage`, { keys: keysToRemove });
  }
}

function clearAuthCookies(isDev: boolean): void {
  if (typeof document === 'undefined') return;

  const cookies = document.cookie ? document.cookie.split(';') : [];
  cookies.forEach((cookie) => {
    const [rawName] = cookie.split('=');
    const name = rawName?.trim();
    if (!name) return;

    const lowerName = name.toLowerCase();
    const shouldClear = lowerName.includes('sb-') || lowerName.includes('supabase');
    if (!shouldClear) return;

    const base = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; samesite=lax`;
    document.cookie = base;
    document.cookie = `${base}; domain=.mindhunters.io`;

    if (isDev) {
      console.log('[Auth Trace] Cleared cookie', { name });
    }
  });
}

function runAuthStorageCleanup(isDev: boolean): boolean {
  if (typeof window === 'undefined') return false;

  try {
    if (window.sessionStorage.getItem(STORAGE_CLEAN_FLAG) === '1') {
      return false;
    }
  } catch (error) {
    if (isDev) {
      console.warn('[Auth Trace] Failed to read sessionStorage cleanup flag', error);
    }
  }

  try {
    clearAuthStorage(window.localStorage, 'localStorage', isDev);
  } catch (error) {
    console.warn('[Auth Trace] Failed to access localStorage', error);
  }

  try {
    clearAuthStorage(window.sessionStorage, 'sessionStorage', isDev);
  } catch (error) {
    console.warn('[Auth Trace] Failed to access sessionStorage', error);
  }

  clearAuthCookies(isDev);

  try {
    window.sessionStorage.setItem(STORAGE_CLEAN_FLAG, '1');
  } catch (error) {
    if (isDev) {
      console.warn('[Auth Trace] Failed to set sessionStorage cleanup flag', error);
    }
  }

  return true;
}

export function AuthTraceClient() {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const isDev = process.env.NODE_ENV === 'development';

    if (SHOULD_CLEAN_AUTH_STORAGE) {
      runAuthStorageCleanup(isDev);
      traceMark('AuthTraceClient storage cleanup enabled');
    }

    if ((window as Window & { __MH_AUTH_TRACE_INSTALLED__?: boolean }).__MH_AUTH_TRACE_INSTALLED__) {
      traceMark('AuthTraceClient mounted (already installed)');
      return;
    }

    (window as Window & { __MH_AUTH_TRACE_INSTALLED__?: boolean }).__MH_AUTH_TRACE_INSTALLED__ = true;

    const originalFetch = window.fetch.bind(window);
    const originalXHROpen = window.XMLHttpRequest.prototype.open;

    window.fetch = async (...args) => {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url;

      if (shouldTraceRefresh(url)) {
        logAuthStormTrace(url ?? 'unknown');
      }

      return originalFetch(...args);
    };

    window.XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null
    ) {
      const urlString = typeof url === 'string' ? url : url.toString();
      if (shouldTraceRefresh(urlString)) {
        logAuthStormTrace(urlString);
      }

      return originalXHROpen.call(this, method, url, async ?? true, username ?? null, password ?? null);
    };

    traceMark('AuthTraceClient mounted');

    return () => {
      window.fetch = originalFetch;
      window.XMLHttpRequest.prototype.open = originalXHROpen;
    };
  }, []);

  return null;
}
